import { ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import type { CourseTreeNode } from "@/types/generated";
import TrainerChapterNode from "@/components/course/tree/TrainerChapterNode";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { stripCourseNavQueryString } from "@/lib/chapterNavigation";
import {
  treeChromeClass,
  treeTitleClass,
} from "@/components/course/tree/treeStyles";
import { cn } from "@/lib/utils";

interface TrainerModuleNodeProps {
  module: CourseTreeNode;
  isFirst?: boolean;
  isExpanded: boolean;
  onToggle: (moduleId: string) => void;
}

const TrainerModuleNode = ({
  module,
  isFirst,
  isExpanded,
  onToggle,
}: TrainerModuleNodeProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = useCourseBasePath();

  const chapters =
    module.children?.filter((child) => child.type === "chapter") ?? [];
  const hasChapters = chapters.length > 0;

  return (
    <div
      id={`node-${module.id}`}
      className={!isFirst ? "border-t border-gray-200" : ""}
    >
      <div className="w-full flex items-center gap-2 px-5 py-2.5 hover:bg-gray-50 transition-colors">
        <button
          onClick={() =>
            navigate(
              `${basePath}/${module.id}${stripCourseNavQueryString(location.search)}`
            )
          }
          className="flex-1 min-w-0 text-left cursor-pointer"
          title={module.title}
        >
          <span
            className={cn(
              "text-[18px] font-bold wrap-break-word leading-snug",
              treeTitleClass(false)
            )}
          >
            {module.title}
          </span>
        </button>
        {hasChapters && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(module.id);
            }}
            aria-expanded={isExpanded}
            aria-controls={`chapters-${module.id}`}
            aria-label={isExpanded ? "Modul einklappen" : "Modul ausklappen"}
            className="shrink-0 p-1 rounded hover:bg-gray-100 cursor-pointer"
          >
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                treeChromeClass,
                isExpanded && "rotate-180"
              )}
            />
          </button>
        )}
      </div>

      {isExpanded && hasChapters && (
        <div id={`chapters-${module.id}`} className="pb-1">
          {chapters.map((chapter) => (
            <TrainerChapterNode key={chapter.id} chapter={chapter} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TrainerModuleNode;
